import React, { useEffect } from 'react';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import FormInput from '../../../../components/form/FormInput';
import FormToggle from '../../../../components/form/FormToggle';
import { productAdd, productUpdate } from '../../../../store/slices/tenantSlices/productSlice';
import FormSelect from '../../../../components/form/FormSelect';
import { productTypeOptions, subProductTypeOptions, uomOptions } from '../../../../utils/common';

const AddEditProductModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const dispatch = useDispatch();

  const initialValues = {
    name: '',
    uom: '',
    product_type: '',
    product_sub_type: '',
    rate: 0,
    status: 'true'
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('Name is required'),
    uom: Yup.string().required('UOM is required'),
    product_type: Yup.string().required('Product type is required'),
    product_sub_type: Yup.string().required('Product sub type is required')
  });

  const onSubmit = async () => {
    if (isEdit) {
      try {
        const payload = { ...values, id: editRecord.id };
        const response = await dispatch(productUpdate(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Updated Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    } else {
      try {
        const payload = { ...values };
        const response = await dispatch(productAdd(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Added Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  useEffect(() => {
    if (isEdit) {
      setValues({
        ...editRecord,
        status: String(editRecord?.status)
      });
    }
  }, [editRecord]);

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit Beat' : 'Add Beat'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="beat" onSubmit={handleSubmit}>
          <Row>
            <Col lg="3">
              <FormInput
                name="name"
                label="Name"
                placeholder="Enter Name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="product_type"
                label="Product Type"
                placeholder="Select Product Type"
                value={values.product_type}
                options={productTypeOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="product_sub_type"
                label="Product Sub Type"
                placeholder="Select Product Type"
                value={values.product_sub_type}
                options={subProductTypeOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="uom"
                label="UOM"
                placeholder="Select UOM"
                value={values.uom}
                options={uomOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormInput
                name="rate"
                label="Rate"
                placeholder="Enter rate"
                value={values.rate}
                onChange={(e) => {
                  let rawValue = e.target.value;
                  if (!values.rate && rawValue === '.') {
                    rawValue = '0.';
                  }
                  if (rawValue.indexOf('.') !== -1) {
                    const parts = rawValue.split('.');
                    rawValue = parts[0] + '.' + parts[1].replace(/[^0-9]/g, ''); // Remove extra dots in the decimal part
                  }
                  rawValue = rawValue.replace(/[^0-9.]/g, '');
                  setFieldValue('rate', rawValue);
                }}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="status"
                label="Status"
                value={values.status}
                onChange={(e) => setFieldValue('status', e)}
              />
            </Col>
          </Row>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="beat" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditProductModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditProductModal;
